<script setup lang="ts">
import { useScrollLock } from '@vueuse/core'
import type { CSSProperties } from 'vue'

import BaseButton from '~/components/atoms/BaseButton.vue'
import ArrowBackIcon from '~/components/atoms/icons/ArrowBackIcon.vue'
import CloseIcon from '~/components/atoms/icons/CloseIcon.vue'
import TextButton from '~/components/atoms/TextButton.vue'
import TextTitle from '~/components/foundations/TextTitle.vue'

type OkButton = {
  color?: 'primary' | 'danger'
  text: string
  disabled?: boolean
}
type CancelButton = {
  text: string
}
type Buttons = {
  ok: OkButton
  cancel?: CancelButton
}

const props = withDefaults(
  defineProps<{
    isActive: boolean
    title?: string | undefined
    hideCloseButton?: boolean
    showBack?: boolean
    buttons?: Buttons
    padding?: {
      top: CSSProperties['paddingTop']
      bottom: CSSProperties['paddingBottom']
      left: CSSProperties['paddingLeft']
      right: CSSProperties['paddingRight']
    }
    overflowY?: CSSProperties['overflowY']
  }>(),
  {
    title: undefined,
    hideCloseButton: false,
    showBack: false,
    buttons: undefined,
    padding: () => {
      return {
        top: '24px',
        bottom: '24px',
        left: '16px',
        right: '16px',
      }
    },
    overflowY: 'auto',
  }
)

const emit = defineEmits<{
  'update:isActive': [value: boolean]
  ok: []
  cancel: []
  close: []
  back: []
}>()

const close = () => {
  emit('update:isActive', false)
  emit('close')
}

const okButtonColor = props.buttons?.ok.color || 'primary'

const isLocked = useScrollLock(document.body)

watchSyncEffect(() => {
  isLocked.value = props.isActive
})

watchPostEffect(() => {
  if (props.isActive) {
    // HACK: Safariでダイアログの表示位置と本当の位置がズレるバグの対策でスクロールイベントを発火させる
    nextTick(() => {
      window.scrollBy(0, Number.MIN_VALUE)
    })
  }
})

const paddingTop = computed(() => props.padding.top)
const paddingBottom = computed(() => props.padding.bottom)
const paddingLeft = computed(() => props.padding.left)
const paddingRight = computed(() => props.padding.right)
const bodyPadding = computed(
  () => `${paddingTop.value} ${paddingRight.value} ${paddingBottom.value} ${paddingLeft.value}`
)
</script>

<template>
  <div v-if="isActive">
    <Teleport defer to="#dialog">
      <div class="root">
        <div class="background" @click="close" />
        <div class="container" role="dialog" aria-modal="true" :aria-label="title">
          <div v-if="title" class="header">
            <TextTitle>{{ title }}</TextTitle>
          </div>
          <button v-if="showBack" class="back" @click="emit('back')">
            <ArrowBackIcon :size="20" />
          </button>
          <button v-if="title && !hideCloseButton" class="close" @click="close">
            <CloseIcon />
          </button>
          <div class="body">
            <div>
              <slot name="content" />
            </div>
            <!-- NOTE: BaseDialogにボタンは含めない方が使いやすいかもしれない -->
            <div v-if="buttons" class="submit-area">
              <TextButton
                v-if="buttons.cancel"
                :text="buttons.cancel.text"
                @click="emit('cancel')"
              />

              <BaseButton
                :text="buttons.ok.text"
                :color="okButtonColor"
                :disabled="buttons.ok.disabled"
                @click="emit('ok')"
              />
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
$header-height: 48px;
$dialog-margin: 16px;

.root {
  .background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(10, 10, 10, 0.7);
  }
  .container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100svw - $dialog-margin * 2);
    height: auto;
    max-width: 544px;
    max-height: 672px;
    overflow-y: v-bind(overflowY);
    background-color: white;
    @include mobile {
      max-width: calc(100svw - $dialog-margin * 2);
      max-height: 95svh;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--semantic-tokens-neutral-weak-surface);
      padding: 8px;
      height: $header-height;
    }
    .close {
      cursor: pointer;
      position: absolute;
      right: 0;
      top: 0;
      height: $header-height;

      padding-right: 16px;
      border: none;
      background-color: transparent;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .back {
      cursor: pointer;
      position: absolute;
      left: 0;
      top: 0;
      height: $header-height;

      padding-left: 16px;
      border: none;
      background-color: transparent;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .body {
      padding: v-bind(bodyPadding);

      display: flex;
      flex-direction: column;
      gap: 32px;

      .submit-area {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 16px;
      }
    }
  }
}
</style>
